(() => {
  const Gift = window.SnowcubesGift || {};

  if (!customElements.get("product-form")) {
    customElements.define(
      "product-form",
      class ProductForm extends HTMLElement {
        constructor() {
          super();

        this.form = this.querySelector("form");
        this.submitButton = this.querySelector('[type="submit"]');

        // Some contexts (e.g. placeholder quick-add content) render <product-form>
        // without a real <form>. Avoid crashing on upgrade.
        if (!this.form || !this.submitButton) return;

        const idInput = this.form.querySelector("[name=id]");
        if (idInput) idInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        // Passing no argument will hide the error message if one was on screen because errorText defaults to false
        this.handleErrorMessage();

        const section =
          this.closest("section, .product, [data-section]") || document;
        const variantSelect = section.querySelector("variant-selects, variant-radios");
        const variantTitle = variantSelect?.currentVariant?.title || "";
        const isGiftWrapped = Gift.isGiftWrappedVariantTitle
          ? Gift.isGiftWrappedVariantTitle(variantTitle)
          : false;
        const giftNoteCard = section.querySelector("[data-gift-note-card]");
        const giftAlwaysOn = !!giftNoteCard?.hasAttribute("data-gift-always-on");
        let hasSelectableGiftVariant = false;
        try {
          hasSelectableGiftVariant = !!variantSelect?.findGiftWrappedVariant?.();
        } catch (_) {
          hasSelectableGiftVariant = false;
        }
        const requiresGiftConfig = isGiftWrapped || (giftAlwaysOn && !hasSelectableGiftVariant);
        const giftConfigInput = this.form.querySelector('input[name="properties[_GiftCardConfig]"]');
        let hasGiftConfig = !!giftConfigInput?.value;

        Gift.debug?.("Product form submit", {
          variantTitle,
          isGiftWrapped,
          giftAlwaysOn,
          hasSelectableGiftVariant,
          requiresGiftConfig,
          hasGiftConfig,
        });

        // Gift config interception: If the product requires gift config (either via
        // Gift Wrapped variant OR giftAlwaysOn), ensure we have a config before
        // allowing add-to-cart. This prevents items that need a gift card from
        // reaching checkout without one.
        if (requiresGiftConfig && !hasGiftConfig) {
          // Try to restore from localStorage (prevents mismatch where UI says “saved”
          // but the hidden input was cleared by variant changes).
          try {
            const storedConfig =
              window.giftConfigPrototype?.load ? window.giftConfigPrototype.load() : null;

            if (storedConfig) {
              Gift.debug?.("Restoring gift config from localStorage", Gift.summarizeGiftConfig?.(storedConfig));

              if (Gift.upsertGiftInputs) {
                const result = Gift.upsertGiftInputs(this.form, storedConfig);
                if (!result.ok) {
                  Gift.debug?.("Failed to upsert gift inputs", { error: result.error?.message || String(result.error) });
                }
              } else {
                const configJson = JSON.stringify(storedConfig);

                const upsertHidden = (name, value) => {
                  let input = this.form.querySelector(`input[name="${name}"]`);
                  if (!input) {
                    input = document.createElement("input");
                    input.type = "hidden";
                    input.name = name;
                    this.form.appendChild(input);
                  }
                  input.value = value;
                };

                upsertHidden("properties[_GiftCardConfig]", configJson);
                upsertHidden("properties[_GiftCardTo]", storedConfig.to || "");
                upsertHidden("properties[_GiftCardFrom]", storedConfig.from || "");
                upsertHidden("properties[_GiftCardMessage]", storedConfig.message || "");
              }

              hasGiftConfig = true;
            }
          } catch (e) {
            // If anything goes wrong, we fall back to opening the modal below.
          }
        }

        if (requiresGiftConfig && !hasGiftConfig) {
          // Best UX: open the gift modal instead of silently doing nothing.
          if (typeof window.openGiftBoxModal === "function") {
            Gift.debug?.("Gift config required but missing; opening modal", { isGiftWrapped, giftAlwaysOn });
            window.openGiftBoxModal({ mode: "add" });
          } else {
            this.handleErrorMessage(
              "Please complete your gift card before adding to cart."
            );
          }
          return;
        }

        // Disabling the button temporarily so that the request can be processed
        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");

        const config = {
          method: "POST",
          headers: {
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
        };

        const formData = new FormData(this.form);
        formData.append("sections", ["cart-bubble", "cart-drawer"]);

        config.body = formData;

        const t0 = typeof performance !== "undefined" ? performance.now() : 0;

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const dt = typeof performance !== "undefined" ? performance.now() - t0 : null;
            Gift.debug?.("Cart add response received", {
              ok: response.ok,
              status: response.status,
              contentType: response.headers?.get?.("content-type") || "",
              ms: dt,
            });

            if (!response.ok) {
              const bodyText = await response.text().catch(() => "");
              throw new Error(
                `Cart add failed (${response.status}): ${bodyText.slice(0, 200)}`
              );
            }

            return response.json();
          })
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);

              this.error = true;

              return;
            }

            let cartBubbleOne = document.getElementById("cart-details--sticky");

            if (cartBubbleOne) {
              cartBubbleOne.innerHTML = `${response.sections[
                "cart-bubble"
              ].match(/\d+/)}`;
            }

            let cartBubbleTwo = document.getElementById("cart-details--relative");

            if (cartBubbleTwo) {
              cartBubbleTwo.innerHTML = `${response.sections[
                "cart-bubble"
              ].match(/\d+/)}`;
            }

            const drawerHtml = response.sections?.["cart-drawer"];

            const dialog1 = document.getElementById("dialog-cart-1");
            const dialog1Body = dialog1?.querySelector(".dialog__content__body");
            if (dialog1Body && drawerHtml) dialog1Body.innerHTML = `${drawerHtml}`;

            const dialog2 = document.getElementById("dialog-cart-2");
            const dialog2Body = dialog2?.querySelector(".dialog__content__body");
            if (dialog2Body && drawerHtml) dialog2Body.innerHTML = `${drawerHtml}`;

            if (dialog1?.parentNode) dialog1.parentNode.open = true;
            if (dialog2?.parentNode) dialog2.parentNode.open = true;

            this.error = false;
          })
          .catch((error) => {
            console.error(error);
            // Don't fail silently — surface a message so the user isn't left with a
            // spinner that resets, no item added, and no idea why it didn't work.
            this.handleErrorMessage(
              "Something went wrong adding to cart. Please try again."
            );
            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            this.submitButton.removeAttribute("aria-disabled");
          });
      }

      handleErrorMessage(errorText = false) {
        if (this.hideErrors) return;

        this.errorMessage =
          this.errorMessage || this.querySelector(".message-error");
        if (!this.errorMessage) return;

        this.errorMessage.classList.toggle("hidden", !errorText);

        const svg = this.errorMessage.querySelector("svg");
        let span = this.errorMessage.querySelector("span");

        if (errorText) {
          if (!span) {
            span = document.createElement("span");
            if (svg) {
              svg.after(span);
            } else {
              this.errorMessage.appendChild(span);
            }
          }
          span.textContent = errorText;
        } else if (span) {
          span.remove();
        }
      }
      }
    );
  }
})();
